import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { User as UserIcon } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { Can } from '@/features/sessao/providers/auth-context'
import UserSelector from '@/features/usuarios/components/user-selector'
import { Call, User } from '@/services/backend/config/entities'
import { Repository } from '@/services/repository'

type Props = {
  callId: number
  callStatus: string
  onSuccess: (data: Call) => void
}

function useFormHook({ callId, onSuccess }: Props) {
  const { TechAssign } = Repository.Entity.Call
  const [user, setUser] = useState<Omit<User, 'password'>>()

  const form = useForm<Parameters<typeof TechAssign>[0]>({
    defaultValues: { id: callId },
  })

  const onSubmit = form.handleSubmit(async (data) => {
    if (!user) return toast.error('Selecione um usuário')

    try {
      const res = await TechAssign({
        ...data,
        techUserId: user.id,
      })
      toast.success('Técnico atribuído com sucesso')
      onSuccess(res)
    } catch (error: any) {
      toast.error(error.message)
    }
  })

  return { onSubmit, user, setUser }
}

function Form(props: Props) {
  const { onSubmit, user, setUser } = useFormHook(props)

  return (
    <form onSubmit={onSubmit}>
      <VStack align="stretch" gap={4}>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Usuário</FormLabel>
          <InputGroup>
            <Input value={user?.displayName ?? ''} isDisabled />
            <InputRightElement>
              <UserSelector role="USER" onSelect={setUser} />
            </InputRightElement>
          </InputGroup>
        </FormControl>

        <HStack justifyContent="space-between" mt={8}>
          <Button type="submit" colorScheme="primary">
            Enviar
          </Button>
        </HStack>
      </VStack>
    </form>
  )
}

export default function Component({ onSuccess, ...props }: Props) {
  const { isOpen, onOpen, onClose } = useDisclosure()

  const isEditable = ['OPEN', 'ASSIGNED'].includes(props.callStatus)
  if (!isEditable) return null

  return (
    <Can asDefault I="update" a="Call" field="techUserId">
      <IconButton
        aria-label="Técnico"
        icon={<Icon as={UserIcon} />}
        onClick={onOpen}
        colorScheme="primary"
      />

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Atribuir técnico</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Form
              onSuccess={(res) => {
                onClose()
                onSuccess(res)
              }}
              {...props}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Can>
  )
}
